import {
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, CaseUser, Note, NotePage, NoteScope, NoteScopeCounts } from "../lib/api";
import { useAuth } from "../lib/auth";

interface Props {
  caseId: number;
  /** Bumped by parent when an outside surface (chat, timeline) saved a new note,
   *  so the list refreshes without a full remount. */
  refreshKey?: number;
  /** When the parent wants to seed a new note (e.g. "Save chat answer to notes"),
   *  it passes a draft here; we open the composer pre-populated with it. */
  pendingDraft?: NoteDraft | null;
  onPendingConsumed?: () => void;
  /** When chat/timeline auto-save a note and the user clicks the "Saved" badge,
   *  the parent passes the note id here so we open it directly. */
  pendingSelectId?: number | null;
  onPendingSelectConsumed?: () => void;
  /** From a chat-sourced note, jump back to the originating chat thread. */
  onOpenChat?: (conversationId: number) => void;
  /** Fired whenever the editor modal closes (via Close, Save, Delete, or Esc).
   *  Lets the parent restore the previous workspace tab when the modal was
   *  opened from chat/timeline rather than from the notes list itself. */
  onModalClosed?: () => void;
}

export interface NoteDraft {
  title?: string;
  body: string;
  category?: string;
  tags?: string[];
  source_type?: "manual" | "chat" | "timeline";
  source_ref?: Record<string, unknown>;
}

const SCOPES: { value: NoteScope; label: string; hint: string }[] = [
  { value: "all", label: "All", hint: "Mine + shared with me" },
  { value: "mine", label: "Mine", hint: "Only notes I created" },
  { value: "shared", label: "Shared", hint: "Shared with me by others" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export default function NotesView({
  caseId,
  refreshKey = 0,
  pendingDraft = null,
  onPendingConsumed,
  pendingSelectId = null,
  onPendingSelectConsumed,
  onOpenChat,
  onModalClosed,
}: Props) {
  const [pageData, setPageData] = useState<NotePage | null>(null);
  const [counts, setCounts] = useState<NoteScopeCounts>({ all: 0, mine: 0, shared: 0 });
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  // ----- filter state -----
  const [scope, setScope] = useState<NoteScope>("all");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [filterTags, setFilterTags] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // ----- modal / editor state -----
  const [activeId, setActiveId] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<NoteDraft | null>(null);
  const [pendingShareIds, setPendingShareIds] = useState<number[]>([]);

  // ----- metadata -----
  const [caseUsers, setCaseUsers] = useState<CaseUser[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);

  // ----- pagination -----
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState<number>(25);

  const [bump, setBump] = useState(0);

  // Debounce the search box so we don't fire a network request on every
  // keystroke. 250ms feels responsive while still cutting down chatter.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Stable representation of the active tag filter for use as a hook dep.
  const tagsKey = useMemo(
    () => Array.from(filterTags).sort().join("|"),
    [filterTags],
  );

  // Build the URL the server should answer. Encapsulated so we can reuse
  // it for the initial load AND for the same-key re-fetch on a save/delete.
  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    p.set("scope", scope);
    if (debouncedSearch.trim()) p.set("q", debouncedSearch.trim());
    if (filterCategory) p.set("category", filterCategory);
    for (const t of filterTags) p.append("tag", t);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    p.set("limit", String(pageSize));
    p.set("offset", String(offset));
    return p.toString();
  }, [scope, debouncedSearch, filterCategory, filterTags, from, to, pageSize, offset]);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const data = await api.get<NotePage>(`/cases/${caseId}/notes?${buildQuery()}`);
      setPageData(data);
      setCounts(data.counts);
      setLoadErr(null);
      setLoaded(true);
    } catch (e: any) {
      setLoadErr(e?.message ?? "Failed to load notes");
      setLoaded(true);
    } finally {
      setBusy(false);
    }
  }, [caseId, buildQuery]);

  // Initial load + react to filter/page changes + outside refreshes.
  useEffect(() => { void reload(); }, [reload, refreshKey, bump]);

  // Any *filter* change resets the page to 0 — otherwise the user could
  // land on an empty page after typing into the search box. (Page-size
  // change also resets, which feels right too.)
  useEffect(
    () => { setOffset(0); },
    [scope, debouncedSearch, filterCategory, tagsKey, from, to, pageSize],
  );

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [users, cats, tags] = await Promise.all([
          api.get<CaseUser[]>(`/cases/${caseId}/users`),
          api.get<string[]>(`/cases/${caseId}/notes/categories`),
          api.get<string[]>(`/cases/${caseId}/notes/tags`),
        ]);
        if (cancel) return;
        setCaseUsers(users);
        setAllCategories(cats);
        setAllTags(tags);
      } catch {
        /* metadata endpoints are best-effort; the editor still works without them. */
      }
    })();
    return () => { cancel = true; };
  }, [caseId, bump]);

  // External "Save to notes" handoff from chat / timeline.
  useEffect(() => {
    if (!pendingDraft) return;
    setActiveId("new");
    setDraft({
      title: pendingDraft.title,
      body: pendingDraft.body,
      category: pendingDraft.category,
      tags: pendingDraft.tags ?? [],
      source_type: pendingDraft.source_type ?? "manual",
      source_ref: pendingDraft.source_ref,
    });
    setPendingShareIds([]);
    onPendingConsumed?.();
  }, [pendingDraft, onPendingConsumed]);

  // External "Open this saved note" handoff (the user clicked the
  // "Saved" pill on a chat answer or timeline event).
  useEffect(() => {
    if (pendingSelectId == null) return;
    setActiveId(pendingSelectId);
    setDraft(null);
    setPendingShareIds([]);
    onPendingSelectConsumed?.();
  }, [pendingSelectId, onPendingSelectConsumed]);

  const items = pageData?.items ?? [];
  const total = pageData?.total ?? 0;

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageNum = Math.floor(offset / pageSize) + 1;
  const pageStartHuman = total === 0 ? 0 : offset + 1;
  const pageEndHuman = Math.min(offset + items.length, total);
  const hasPrev = offset > 0;
  const hasNext = offset + pageSize < total;

  const filtersActive = !!(
    search || filterCategory || filterTags.size > 0 || from || to || scope !== "all"
  );


  function clearFilters() {
    setSearch("");
    setFilterCategory("");
    setFilterTags(new Set());
    setFrom(""); setTo("");
    setScope("all");
  }

  function startNew() {
    setActiveId("new");
    setDraft({ body: "", tags: [], source_type: "manual" });
    setPendingShareIds([]);
  }

  function openExisting(id: number) {
    setActiveId(id);
    setDraft(null);
    setPendingShareIds([]);
  }

  function closeModal() {
    setActiveId(null);
    setDraft(null);
    setPendingShareIds([]);
    onModalClosed?.();
  }

  // The active note may not be on the current page (the user could have
  // jumped to a saved note from chat that lives on a different page), so
  // look it up in the page first then fall back to a fetch-by-id.
  const [activeNote, setActiveNote] = useState<Note | null>(null);
  useEffect(() => {
    if (activeId === null || activeId === "new") {
      setActiveNote(null);
      return;
    }
    const onPage = items.find((n) => n.id === activeId) ?? null;
    if (onPage) {
      setActiveNote(onPage);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const fresh = await api.get<Note>(`/cases/${caseId}/notes/${activeId}`);
        if (!cancelled) setActiveNote(fresh);
      } catch {
        if (!cancelled) setActiveNote(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, items, caseId]);

  return (
    <div className="notes-page">
      <div className="notes-toolbar">
        <div className="notes-toolbar__row">
          <input
            type="search"
            className="notes-toolbar__search"
            placeholder="Search notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="notes-toolbar__date">
            <span>From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="notes-toolbar__date">
            <span>To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <div className="notes-toolbar__spacer" />
          <button type="button" className="btn btn--primary" onClick={startNew}>
            + New note
          </button>
        </div>

        <div className="notes-toolbar__row notes-toolbar__row--chips">
          <span className="notes-toolbar__label">Scope:</span>
          {SCOPES.map((s) => {
            const c = s.value === "all" ? counts.all : s.value === "mine" ? counts.mine : counts.shared;
            const on = scope === s.value;
            return (
              <button
                key={s.value}
                type="button"
                className={`chip chip--toggle${on ? " chip--on" : ""}`}
                title={s.hint}
                onClick={() => setScope(s.value)}
              >
                {s.label}
                <span className="notes-toolbar__chip-count">{c}</span>
              </button>
            );
          })}

          <span className="notes-toolbar__label notes-toolbar__label--gap">Category:</span>
          <select
            className="notes-toolbar__select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {allCategories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {allTags.length > 0 && (
          <div className="notes-toolbar__row notes-toolbar__row--chips">
            <span className="notes-toolbar__label">Tags:</span>
            <div className="notes-toolbar__tag-row">
              {allTags.map((t) => {
                const on = filterTags.has(t);
                return (
                  <button
                    key={t}
                    type="button"
                    className={`chip chip--toggle${on ? " chip--on" : ""}`}
                    onClick={() => {
                      const next = new Set(filterTags);
                      if (next.has(t)) next.delete(t); else next.add(t);
                      setFilterTags(next);
                    }}
                  >
                    #{t}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="notes-toolbar__status">
          <span className="muted">
            {busy && !loaded
              ? "Loading…"
              : total === 0
                ? `Showing 0 of ${counts.all} notes`
                : `Showing ${pageStartHuman}–${pageEndHuman} of ${total}${
                    filtersActive ? ` (filtered from ${counts.all})` : ""
                  }`}
          </span>
          {filtersActive && (
            <button className="btn btn--ghost btn--small" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="notes-page__list">
        {!loaded && <div className="muted">Loading notes…</div>}
        {loaded && loadErr && <div className="alert alert--error">{loadErr}</div>}

        {loaded && !loadErr && total === 0 && (
          <div className="empty">
            {counts.all === 0 ? (
              <>
                <p>No notes yet for this case.</p>
                <button className="btn btn--primary" onClick={startNew}>+ New note</button>
              </>
            ) : (
              <>
                <p>No notes match these filters.</p>
                <button className="btn btn--ghost btn--small" onClick={clearFilters}>
                  Clear filters
                </button>
              </>
            )}
          </div>
        )}

        {loaded && !loadErr && total > 0 && (
          <>
            <ol className="timeline notes-list">
              {items.map((n) => (
                <NoteRow key={n.id} note={n} onOpen={() => openExisting(n.id)} />
              ))}
            </ol>

            <div className="notes-pagination">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={!hasPrev || busy}
                onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
              >
                ‹ Prev
              </button>
              <span className="muted notes-pagination__status">
                Page {pageNum} of {pageCount}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={!hasNext || busy}
                onClick={() => setOffset((o) => o + pageSize)}
              >
                Next ›
              </button>
              <div className="notes-pagination__spacer" />
              <label className="notes-pagination__size">
                <span className="muted">Per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          </>
        )}
      </div>

      {(activeId === "new" && draft) && (
        <NoteEditorModal onClose={closeModal}>
          <NoteEditor
            key="new"
            mode="new"
            caseId={caseId}
            caseUsers={caseUsers}
            allCategories={allCategories}
            allTags={allTags}
            initial={draft}
            initialShareIds={pendingShareIds}
            onCancel={closeModal}
            onSaved={(n) => {
              setActiveId(n.id);
              setDraft(null);
              setBump((x) => x + 1);
            }}
            onMentionResolved={(ids) => setPendingShareIds(ids)}
            onOpenChat={onOpenChat}
          />
        </NoteEditorModal>
      )}

      {activeNote && (
        <NoteEditorModal onClose={closeModal}>
          <NoteEditor
            key={`edit-${activeNote.id}`}
            mode={activeNote.is_owner ? "edit" : "view"}
            caseId={caseId}
            caseUsers={caseUsers}
            allCategories={allCategories}
            allTags={allTags}
            note={activeNote}
            onCancel={closeModal}
            onSaved={() => setBump((x) => x + 1)}
            onDeleted={() => {
              closeModal();
              setBump((x) => x + 1);
            }}
            onOpenChat={onOpenChat}
          />
        </NoteEditorModal>
      )}
    </div>
  );
}

// ---------- list row (mirrors timeline row layout) ----------

function NoteRow({ note, onOpen }: { note: Note; onOpen: () => void }) {
  const title = (note.title?.trim()) || firstLine(note.body) || "Untitled note";
  const snippet = stripFirstLine(note.body).slice(0, 220);
  return (
    <li className="timeline__item">
      <div
        className="timeline__row"
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            onOpen();
          }
        }}
        title="Open note"
      >
        <div className="timeline__date">
          <time>{relativeTime(note.updated_at)}</time>
          {note.created_at && note.updated_at !== note.created_at && (
            <div className="muted notes-row__created">created {relativeTime(note.created_at)}</div>
          )}
        </div>
        <div className="timeline__body">
          <div className="timeline__head">
            <strong>{title}</strong>
            <div className="notes-row__head-meta">
              {note.category && <span className="chip notes-row__cat">{note.category}</span>}
              {note.is_shared_with_me && (
                <span
                  className="badge badge--shared"
                  title={`Shared with you by ${note.owner_display_name ?? note.owner_email ?? "someone"}`}
                >
                  shared
                </span>
              )}
              {note.source_type !== "manual" && (
                <span className="chip notes-row__source">from {note.source_type}</span>
              )}
            </div>
          </div>
          {snippet && <p className="muted notes-row__snippet">{snippet}</p>}
          <div className="notes-row__foot">
            {note.tags.length > 0 && (
              <div className="timeline__actors">
                {note.tags.map((t) => (
                  <span key={t} className="chip">#{t}</span>
                ))}
              </div>
            )}
            <span className="muted notes-row__byline">
              {note.is_shared_with_me
                ? `by ${note.owner_display_name ?? note.owner_email ?? "—"}`
                : note.shared_with.length > 0
                  ? `shared with ${note.shared_with.length}`
                  : "private"}
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}

// ---------- modal shell ----------

function NoteEditorModal({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  // Esc-to-close + body scroll lock while modal is open.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal__card modal__card--lg modal__card--split notes-modal"
        onClick={(ev) => ev.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ---------- editor ----------

interface EditorProps {
  caseId: number;
  caseUsers: CaseUser[];
  allCategories: string[];
  allTags: string[];
  mode: "new" | "edit" | "view";
  note?: Note;
  initial?: NoteDraft;
  initialShareIds?: number[];
  onCancel: () => void;
  onSaved: (n: Note) => void;
  onDeleted?: () => void;
  onMentionResolved?: (ids: number[]) => void;
  /** Jump back to the chat conversation this note was captured from. */
  onOpenChat?: (conversationId: number) => void;
}

function NoteEditor({
  caseId,
  caseUsers,
  allCategories,
  allTags,
  mode,
  note,
  initial,
  initialShareIds = [],
  onCancel,
  onSaved,
  onDeleted,
  onMentionResolved,
  onOpenChat,
}: EditorProps) {
  const isView = mode === "view";
  const [title, setTitle] = useState<string>(initial?.title ?? note?.title ?? "");
  const [body, setBody] = useState<string>(initial?.body ?? note?.body ?? "");
  const [category, setCategory] = useState<string>(initial?.category ?? note?.category ?? "");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? note?.tags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [shareIds, setShareIds] = useState<number[]>(
    initialShareIds.length > 0
      ? initialShareIds
      : note?.shared_with.map((s) => s.user_id) ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  // Mention autocomplete state.
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [mentionHighlight, setMentionHighlight] = useState(0);

  useEffect(() => { onMentionResolved?.(shareIds); }, [shareIds, onMentionResolved]);

  // Auto-grow body textarea as content changes. We deliberately do NOT
  // cap the max height: the surrounding modal body is the single scroll
  // container per our modal-structure rule, and capping the textarea here
  // re-introduces a second (inner) scrollbar whenever the captured chat
  // answer is longer than the cap.
  //
  // Border-width compensation: with the global `box-sizing: border-box`,
  // an element's `height` style includes its borders, but `scrollHeight`
  // only covers padding + content. Setting `height = scrollHeight` would
  // therefore leave the inner content area `borderTop + borderBottom`
  // (≈2px) short, causing the textarea to grow its OWN scrollbar — the
  // exact "second scrollbar" bug we're trying to avoid.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = "auto";
    const cs = window.getComputedStyle(el);
    const border =
      (parseFloat(cs.borderTopWidth) || 0) +
      (parseFloat(cs.borderBottomWidth) || 0);
    el.style.height = `${Math.max(el.scrollHeight + border, 240)}px`;
  }, [body]);

  const userById = useMemo(() => {
    const m = new Map<number, CaseUser>();
    for (const u of caseUsers) m.set(u.id, u);
    return m;
  }, [caseUsers]);

  const mentionSuggestions = useMemo<CaseUser[]>(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return caseUsers
      .filter((u) => {
        if (q && !`${u.display_name} ${u.email}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice(0, 8);
  }, [mention, caseUsers]);

  function detectMention(value: string, caret: number) {
    // Walk backwards from the caret to find a word starting with @ that
    // hasn't been broken by whitespace yet. We only open the picker while
    // the @-token is still being typed (no whitespace inside).
    let i = caret - 1;
    while (i >= 0) {
      const ch = value[i];
      if (ch === "@") {
        if (i > 0 && /\S/.test(value[i - 1])) {
          // @ embedded in another word (e.g. an email) — don't trigger.
          break;
        }
        setMention({ start: i, query: value.slice(i + 1, caret) });
        setMentionHighlight(0);
        return;
      }
      if (/\s/.test(ch)) break;
      i--;
    }
    setMention(null);
  }

  function applyMention(user: CaseUser) {
    if (!mention) return;
    const before = body.slice(0, mention.start);
    const after = body.slice((bodyRef.current?.selectionStart ?? mention.start));
    // Normalize the inserted handle so it's a single token (no spaces) — we
    // join the user's display name with non-breaking joiners would be
    // overkill; underscores read fine and survive plain-text export.
    const handle = `@${user.display_name.replace(/\s+/g, "_")}`;
    const next = `${before}${handle} ${after}`;
    setBody(next);
    setMention(null);
    if (!shareIds.includes(user.id)) {
      setShareIds([...shareIds, user.id]);
    }
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (!el) return;
      const pos = before.length + handle.length + 1;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }

  function onBodyKeyDown(ev: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (mention && mentionSuggestions.length > 0) {
      if (ev.key === "ArrowDown") {
        ev.preventDefault();
        setMentionHighlight((h) => Math.min(h + 1, mentionSuggestions.length - 1));
        return;
      }
      if (ev.key === "ArrowUp") {
        ev.preventDefault();
        setMentionHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (ev.key === "Enter" || ev.key === "Tab") {
        ev.preventDefault();
        applyMention(mentionSuggestions[mentionHighlight]);
        return;
      }
      if (ev.key === "Escape") {
        ev.preventDefault();
        setMention(null);
        return;
      }
    }
  }

  function commitTagInput() {
    const v = tagInput.trim();
    if (!v) return;
    const dedup = new Set(tags.map((t) => t.toLowerCase()));
    if (!dedup.has(v.toLowerCase())) {
      setTags([...tags, v.slice(0, 64)]);
    }
    setTagInput("");
  }

  function removeTag(t: string) {
    setTags(tags.filter((x) => x !== t));
  }

  function toggleShare(uid: number) {
    if (shareIds.includes(uid)) {
      setShareIds(shareIds.filter((x) => x !== uid));
    } else {
      setShareIds([...shareIds, uid]);
    }
  }

  async function save() {
    if (isView) return;
    if (!body.trim()) {
      setErr("Note body cannot be empty.");
      return;
    }
    setSaving(true); setErr(null);
    try {
      if (mode === "new") {
        const created = await api.post<Note>(`/cases/${caseId}/notes`, {
          title: title.trim() || undefined,
          body: body.trim(),
          category: category.trim() || undefined,
          tags,
          source_type: initial?.source_type ?? "manual",
          source_ref: initial?.source_ref,
          share_with: shareIds,
        });
        onSaved(created);
        setSavedTick((t) => t + 1);
      } else if (note) {
        const updated = await api.patch<Note>(`/cases/${caseId}/notes/${note.id}`, {
          title: title.trim() || null,
          body: body.trim(),
          category: category.trim() || null,
          tags,
        });
        // Sync the share list separately if it changed — keeps the PATCH
        // body simple and matches the dedicated /shares endpoint semantics.
        const currentShareIds = note.shared_with.map((s) => s.user_id).sort();
        const desired = [...shareIds].sort();
        if (currentShareIds.join(",") !== desired.join(",")) {
          await api.put<Note>(`/cases/${caseId}/notes/${note.id}/shares`, {
            user_ids: shareIds,
          });
        }
        onSaved(updated);
        setSavedTick((t) => t + 1);
      }
      // Close the modal after a successful save so the user is returned
      // to wherever they came from (chat answer, timeline, or the notes
      // list itself). The parent's onSaved callback already bumped the
      // list refresh, so re-opening will show the latest state.
      onCancel();
    } catch (e: any) {
      setErr(e?.message ?? "Failed to save note");
    } finally {
      setSaving(false);
    }
  }

  async function destroy() {
    if (!note) return;
    if (!confirm(`Delete note "${(note.title?.trim() || firstLine(note.body) || "Untitled").slice(0, 80)}"?`)) {
      return;
    }
    try {
      await api.del<void>(`/cases/${caseId}/notes/${note.id}`);
      onDeleted?.();
    } catch (e: any) {
      setErr(e?.message ?? "Failed to delete");
    }
  }

  const viewerId = useAuth((s) => s.user?.id ?? 0);

  async function leaveShare() {
    if (!note || !viewerId) return;
    try {
      // Recipient self-revoke: removes the note from "Shared with me".
      await api.del<void>(`/cases/${caseId}/notes/${note.id}/shares/${viewerId}`);
      onDeleted?.();
    } catch (e: any) {
      setErr(e?.message ?? "Failed to leave share");
    }
  }

  return (
    <>
      <header className="modal__header note-editor__head">
        <input
          className="note-editor__title"
          placeholder="Untitled note"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isView}
          aria-label="Note title"
        />
        <button
          type="button"
          className="modal__close"
          onClick={onCancel}
          aria-label="Close"
          title="Close (Esc)"
        >
          ×
        </button>
      </header>

      <div className="modal__body note-editor">
        {note?.is_shared_with_me && (
          <div className="note-editor__banner">
            Shared with you by <strong>{note.owner_display_name ?? note.owner_email ?? "—"}</strong>
            {" · read-only"}
          </div>
        )}

        {initial?.source_type && initial.source_type !== "manual" && (
          <div className="note-editor__banner note-editor__banner--source">
            Capturing from {initial.source_type}
          </div>
        )}

        {err && <div className="alert alert--error">{err}</div>}

        <div className="note-editor__meta-row">
        <label className="note-editor__field">
          <span className="muted">Category</span>
          <input
            type="text"
            list={`categories-${caseId}`}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Strategy, Research, Witness…"
            disabled={isView}
          />
          <datalist id={`categories-${caseId}`}>
            {allCategories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>

        <div className="note-editor__field note-editor__tags">
          <span className="muted">Tags</span>
          <div className="note-editor__tag-chips">
            {tags.map((t) => (
              <span key={t} className="chip chip--on">
                #{t}
                {!isView && (
                  <button
                    type="button"
                    className="chip__x"
                    aria-label={`Remove ${t}`}
                    onClick={() => removeTag(t)}
                  >×</button>
                )}
              </span>
            ))}
            {!isView && (
              <input
                className="note-editor__tag-input"
                placeholder={tags.length === 0 ? "Add tags (Enter)" : "Add"}
                value={tagInput}
                list={`tags-${caseId}`}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    commitTagInput();
                  } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
                    setTags(tags.slice(0, -1));
                  }
                }}
                onBlur={commitTagInput}
              />
            )}
            <datalist id={`tags-${caseId}`}>
              {allTags.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
        </div>
      </div>

      <div className="note-editor__body-wrap">
        <textarea
          ref={bodyRef}
          className="note-editor__body"
          placeholder="Write your note. Use @ to mention case members and share this note with them."
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            const caret = e.target.selectionStart ?? e.target.value.length;
            detectMention(e.target.value, caret);
          }}
          onKeyDown={onBodyKeyDown}
          onSelect={(e) => {
            const tgt = e.target as HTMLTextAreaElement;
            detectMention(tgt.value, tgt.selectionStart ?? 0);
          }}
          disabled={isView}
        />
        {mention && mentionSuggestions.length > 0 && (
          <ul className="mention-menu" role="listbox">
            {mentionSuggestions.map((u, i) => (
              <li
                key={u.id}
                role="option"
                aria-selected={i === mentionHighlight}
                className={`mention-menu__opt${i === mentionHighlight ? " mention-menu__opt--hl" : ""}`}
                onMouseDown={(ev) => { ev.preventDefault(); applyMention(u); }}
                onMouseEnter={() => setMentionHighlight(i)}
              >
                <strong>{u.display_name}</strong>
                <span className="muted"> · {u.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Share pane — visible to the owner (in both edit and new modes) so
          they can see exactly who has access and add/remove members
          without going through the @mention shortcut. */}
      {!isView && (
        <SharePicker
          caseUsers={caseUsers}
          shareIds={shareIds}
          onToggle={toggleShare}
        />
      )}
      {isView && note && note.shared_with.length > 0 && (
        <div className="note-editor__share-row note-editor__share-row--view">
          <span className="muted">Also shared with:</span>
          {note.shared_with.map((s) => (
            <span key={s.user_id} className="chip">{s.display_name}</span>
          ))}
        </div>
      )}

      {note?.source_type === "chat" && note.source_ref && (() => {
        const ref = note.source_ref as {
          query_id?: number;
          conversation_id?: number;
          parent_question?: string;
          kind?: string;
        };
        const isSnippet = ref.kind === "selection";
        return (
          <div className="note-editor__source">
            <span className="muted">
              Captured from a chat
              {isSnippet ? " selection" : " answer"}
              {typeof ref.query_id === "number" && ` · query #${ref.query_id}`}
            </span>
            {ref.parent_question && (
              <div className="note-editor__source-q muted">
                Q: <em>"{truncateText(ref.parent_question, 160)}"</em>
              </div>
            )}
            {typeof ref.conversation_id === "number" && onOpenChat && (
              <button
                type="button"
                className="btn btn--ghost btn--small note-editor__source-jump"
                onClick={() => onOpenChat(ref.conversation_id as number)}
                title="Open the original chat thread"
              >
                Open in chat ↗
              </button>
            )}
          </div>
        );
      })()}
      {note?.source_type === "timeline" && note.source_ref && (
        <div className="note-editor__source muted">
          Captured from a timeline event
          {typeof (note.source_ref as any).provenance_token === "string"
            && ` · ${(note.source_ref as any).provenance_token}`}
        </div>
      )}
      {/* New (unsaved) selection-capture from chat: show the parent question
          inline so the user has context while filling in tags / category. */}
      {!note && initial?.source_type === "chat" && initial.source_ref && (() => {
        const ref = initial.source_ref as {
          parent_question?: string;
          kind?: string;
        };
        if (!ref.parent_question) return null;
        return (
          <div className="note-editor__source">
            <span className="muted">
              {ref.kind === "selection"
                ? "Highlighted from a chat answer"
                : "From a chat answer"}
            </span>
            <div className="note-editor__source-q muted">
              Q: <em>"{truncateText(ref.parent_question, 160)}"</em>
            </div>
          </div>
        );
      })()}

      <div className="note-editor__foot muted">
        {note?.created_at && <>Created {relativeTime(note.created_at)}</>}
        {note?.updated_at && <> · Updated {relativeTime(note.updated_at)}</>}
      </div>

      {/* Suppress unused warnings for helper. */}
      <span hidden>{userById.size}</span>
      </div>

      <footer className="modal__footer">
        <span className="muted modal__footer-hint">
          {savedTick > 0 ? "Saved" : ""}
        </span>
        <div className="modal__footer-actions">
          <button type="button" className="btn" onClick={onCancel}>
            Close
          </button>
          {mode === "view" && note?.is_shared_with_me && (
            <button type="button" className="btn" onClick={leaveShare}>
              Leave shared note
            </button>
          )}
          {mode === "edit" && (
            <button type="button" className="btn" onClick={destroy}>
              Delete
            </button>
          )}
          {!isView && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={save}
              disabled={saving || !body.trim()}
            >
              {saving ? "Saving…" : mode === "new" ? "Create note" : "Save"}
            </button>
          )}
        </div>
      </footer>
    </>
  );
}

function SharePicker({
  caseUsers,
  shareIds,
  onToggle,
}: {
  caseUsers: CaseUser[];
  shareIds: number[];
  onToggle: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const set = new Set(shareIds);
  const filtered = caseUsers.filter((u) => {
    if (!q) return true;
    return `${u.display_name} ${u.email}`.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="note-editor__share">
      <div className="note-editor__share-row">
        <span className="muted">Shared with</span>
        {shareIds.length === 0 ? (
          <span className="muted">no one yet — type @ in the note or use the picker</span>
        ) : (
          shareIds.map((id) => {
            const u = caseUsers.find((x) => x.id === id);
            return (
              <span key={id} className="chip chip--on">
                {u ? u.display_name : `User #${id}`}
                <button
                  type="button"
                  className="chip__x"
                  aria-label="Remove"
                  onClick={() => onToggle(id)}
                >×</button>
              </span>
            );
          })
        )}
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Done" : "Share…"}
        </button>
      </div>
      {open && (
        <div className="note-editor__share-pop">
          <input
            type="search"
            placeholder="Find a case member…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          <ul className="note-editor__share-list">
            {filtered.length === 0 && (
              <li className="muted">No case members match.</li>
            )}
            {filtered.map((u) => {
              const on = set.has(u.id);
              return (
                <li key={u.id}>
                  <label className="note-editor__share-opt">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => onToggle(u.id)}
                    />
                    <span>
                      <strong>{u.display_name}</strong>
                      <span className="muted"> · {u.email}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------

function firstLine(s: string): string {
  return (s.split(/\r?\n/, 1)[0] ?? "").trim();
}
function truncateText(s: string, n: number): string {
  const t = s.trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}
function stripFirstLine(s: string): string {
  return (s.split(/\r?\n/).slice(1).join(" ") || s).trim();
}
function relativeTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (!Number.isFinite(diff)) return "";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
